import {
  ENTITLEMENT_NOTE_MAX_LENGTH,
  EntitlementRevokeSchema,
  SELF_MANAGED_ENTITLEMENT_STORES,
  type UserEntitlement,
} from '@aysar/contracts';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { type FieldDef } from '@/shared/components/dynamic-form/types/dynamic-form.types';
import { useHasPermission } from '@/shared/hooks/useHasPermission/useHasPermission';
import { useToast } from '@/shared/hooks/useToast/useToast';
import { useResolveUserIds } from '@/shared/services/user-lookup/user-lookup.api';
import { toApiError } from '@/shared/utils/transform-error/transform-error';
import { type ApiError } from '@/shared/utils/transform-error/transform-error.types';

import {
  useMemberEntitlementsQuery,
  useRevokeEntitlementMutation,
} from '../../services/entitlements/entitlements.api';
import { entitlementErrorCopy } from '../../utils/billing-error/billing-error.util';

export interface UseEntitlementRevokeModalLogicInput {
  entitlementId: string;
  userId: string;
  onDone: () => void;
}

export interface RevokeValues {
  reason: string;
  /** Present only when a store is still billing for this. Never sent. */
  confirmPhrase?: string;
}

export interface UseEntitlementRevokeModalLogic {
  title: string;
  description: string;
  submitLabel: string;
  fields: FieldDef[];
  schema: z.ZodType<RevokeValues, RevokeValues>;
  initialValues: RevokeValues;
  /** The store this entitlement came from, translated — `''` until the row lands. */
  storeLabel: string;
  /** "This is an App Store entitlement" — the store, in words. */
  storeWarningTitle: string;
  storeWarningBody: string;
  /** Whether a store, not this platform, owns the subscription. */
  isStoreBilled: boolean;
  /** The sentence that says the charges continue, or `null`. */
  stillBillingWarning: string | null;
  /** The address the operator must type back, or `null`. */
  confirmPhrase: string | null;
  /** Withholds the form until the row is known — RHF reads defaults once. */
  isLoading: boolean;
  /** The id names nothing in this member's list any more. Not the same as loading. */
  isMissing: boolean;
  /** The refusal, translated, or `null`. Drawn INSIDE the dialog. */
  error: string | null;
  isConflict: boolean;
  isGone: boolean;
  reload: () => void;
  submit: (values: RevokeValues) => Promise<void>;
}

/**
 * Taking an entitlement back.
 *
 * ## The reason is REQUIRED, which is why this is a dialog
 *
 * `EntitlementRevokeSchema` is `reason: z.string().trim().min(1)`, and a required
 * field belongs to a second dialog rather than to an optional key on a
 * confirmation (P20b) — the same shape `OrderDecisionModal` already has for
 * reject and refund. The api's own argument for demanding it is that this removes
 * something the member may have paid for.
 *
 * ## It applies to ANY store, and that is the thing to say out loud
 *
 * Nothing in the contract or the service restricts the store, so this dialog can
 * end an `app_store` subscription while Apple carries on charging for it — the
 * platform mirrors the store's truth, it does not own it. A confirmation that
 * says "the member will lose access" without saying "and they are still being
 * charged" is worse than no confirmation, because it reads as complete.
 *
 * `SELF_MANAGED_ENTITLEMENT_STORES` decides which half applies, read rather than
 * restated: it is already the one place the platform records which stores it
 * expires on its own clock.
 *
 * ## The phrase is spent where the money is
 *
 * A comp or a direct payment is this platform's to end, and demanding a typed
 * address every time would train operators to type without reading — the habit
 * `confirmPhrase`'s own docblock exists to prevent. A store-billed subscription
 * is the case where a wrong click and a right one look identical *and* the
 * consequence reaches somebody's bank statement, so that one asks for the
 * member's address, read off the row. It is dropped when the address cannot be
 * resolved, because a dialog nobody can confirm is worse than none (P22b4).
 *
 * ## It re-reads rather than being handed the row
 *
 * `ModalData` is serializable-only, so what travels is two ids — and that is the
 * safer design: a row smuggled through the store would be a snapshot of a status
 * a colleague, or a refund webhook, may have changed since the page rendered.
 * "The id names nothing" is therefore a real state of its own.
 */
export function useEntitlementRevokeModalLogic({
  entitlementId,
  userId,
  onDone,
}: UseEntitlementRevokeModalLogicInput): UseEntitlementRevokeModalLogic {
  const { t } = useTranslation('payments');
  const { can } = useHasPermission();
  const toast = useToast();
  const [refusal, setRefusal] = useState<ApiError | null>(null);

  const query = useMemberEntitlementsQuery(userId, { skip: userId === '' });
  const [revokeEntitlement] = useRevokeEntitlementMutation();

  const rows = useMemo(() => query.data?.entitlements ?? [], [query.data]);
  const entitlement = rows.find((row) => row.id === entitlementId) ?? null;

  const isStoreBilled = entitlement !== null && !isSelfManaged(entitlement);

  // Not an empty request — NO request (P21b).
  const memberIds = useMemo(
    () => (can('members.read') && userId !== '' ? [userId] : []),
    [can, userId],
  );
  const { items: memberOptions } = useResolveUserIds(memberIds);
  const memberEmail = memberOptions[0]?.label ?? null;

  const confirmPhrase = isStoreBilled ? memberEmail : null;

  const schema = useMemo(() => buildSchema(confirmPhrase), [confirmPhrase]);

  const fields = useMemo<FieldDef[]>(() => {
    const defined: FieldDef[] = [
      {
        name: 'reason',
        kind: 'textarea',
        label: t('entitlements.revoke.reason'),
        description: t('entitlements.revoke.reasonHint'),
        isRequired: true,
        rows: 3,
        maxLength: ENTITLEMENT_NOTE_MAX_LENGTH,
        colSpan: 'full',
      },
    ];

    if (confirmPhrase !== null) {
      defined.push({
        name: 'confirmPhrase',
        kind: 'text',
        label: t('entitlements.revoke.typeEmail', { email: confirmPhrase }),
        description: t('entitlements.revoke.typeEmailHint'),
        isRequired: true,
        colSpan: 'full',
        // The refinement reaches the form as Zod's `custom` code, which carries
        // no sentence the kit can classify (P10.6e2).
        invalidMessage: t('entitlements.revoke.typeEmailInvalid', { email: confirmPhrase }),
      });
    }

    return defined;
  }, [t, confirmPhrase]);

  const copy = entitlementErrorCopy(refusal);

  const submit = useCallback(
    async (values: RevokeValues) => {
      try {
        // Built key by key rather than spread: `confirmPhrase` is a control the
        // api has never heard of, and `EntitlementRevokeSchema` is `.strict()`.
        await revokeEntitlement({ id: entitlementId, userId, reason: values.reason }).unwrap();
        setRefusal(null);
        toast.success(t('entitlements.done.revoked'));
        onDone();
      } catch (reason) {
        // Held in state and drawn in the dialog rather than thrown at the form's
        // field mapper: neither refusal names a field, and a form-level sentence
        // under the reason box offers no way out of a race somebody else won.
        setRefusal(toApiError(reason));
      }
    },
    [revokeEntitlement, entitlementId, userId, toast, t, onDone],
  );

  const storeLabel = entitlement === null ? '' : t(`entitlements.store.${entitlement.store}`);

  return {
    title: t('entitlements.revoke.title'),
    description: t('entitlements.revoke.subtitle'),
    submitLabel: t('entitlements.revoke.confirm'),
    fields,
    schema,
    initialValues: { reason: '' },
    storeLabel,
    storeWarningTitle: t('entitlements.revoke.storeTitle', { store: storeLabel }),
    storeWarningBody: t('entitlements.revoke.storeBody'),
    isStoreBilled,
    stillBillingWarning: isStoreBilled ? t('entitlements.revoke.stillBilling') : null,
    confirmPhrase,
    isLoading: query.isLoading,
    isMissing: !query.isLoading && entitlement === null,
    error: refusal === null ? null : copy === null ? refusal.message : t(copy.i18nKey),
    isConflict: copy?.isConflict ?? false,
    isGone: copy?.isGone ?? false,
    reload: () => {
      setRefusal(null);
      void query.refetch();
    },
    submit,
  };
}

/**
 * Whether this platform, rather than a store, owns the subscription.
 *
 * `SELF_MANAGED_ENTITLEMENT_STORES` is the contract's own list of the stores the
 * expiry sweep is allowed to age out — which is exactly the same question, asked
 * once. Restating it here would be a second copy that drifts the day a fifth
 * store arrives.
 */
function isSelfManaged(entitlement: UserEntitlement): boolean {
  return SELF_MANAGED_ENTITLEMENT_STORES.includes(entitlement.store);
}

/**
 * The form's shape, composed from the contract rather than restated (U3).
 *
 * `confirmPhrase` is a control the api has never heard of, so it cannot be the
 * contract's job — and the comparison matches `confirmStore`'s own: trimmed, and
 * case-**sensitive**, because an address has one spelling and accepting a near
 * miss would undo the point of asking.
 */
function buildSchema(confirmPhrase: string | null): z.ZodType<RevokeValues, RevokeValues> {
  if (confirmPhrase === null) {
    return EntitlementRevokeSchema as z.ZodType<RevokeValues, RevokeValues>;
  }

  return EntitlementRevokeSchema.extend({
    confirmPhrase: z.string().refine((typed) => typed.trim() === confirmPhrase.trim()),
  }) as z.ZodType<RevokeValues, RevokeValues>;
}
